import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { findParentWithClass } from 'vscode/vscode/vs/base/browser/dom';
import { DisposableStore, MutableDisposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { SuggestAddon } from './terminalSuggestAddon.js';
import { TERMINAL_CONFIG_SECTION } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminal';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { registerActiveInstanceAction } from '../../../terminal/browser/terminalActions.js';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';

var TerminalSuggestContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/suggest/browser/terminal.suggest.contribution";
let TerminalSuggestContribution = class TerminalSuggestContribution extends DisposableStore {
    static { TerminalSuggestContribution_1 = this; }
    static { this.ID = 'terminal.suggest'; }
    static get(instance) {
        return instance.getContribution(TerminalSuggestContribution_1.ID);
    }
    get addon() { return this._addon.value; }
    constructor(_instance, _processManager, widgetManager, _contextKeyService, _configurationService, _instantiationService) {
        super();
        this._instance = _instance;
        this._contextKeyService = _contextKeyService;
        this._configurationService = _configurationService;
        this._instantiationService = _instantiationService;
        this._addon = ( (new MutableDisposable()));
        this._terminalSuggestWidgetContextKeys = ( (new Set(TerminalContextKeys.suggestWidgetVisible.key)));
        this.add(toDisposable(() => this._addon?.dispose()));
        this._terminalSuggestWidgetVisibleContextKey = TerminalContextKeys.suggestWidgetVisible.bindTo(this._contextKeyService);
    }
    xtermOpen(xterm) {
        this._loadSuggestAddon(xterm.raw);
        this.add(this._contextKeyService.onDidChangeContext(e => {
            if (e.affectsSome(this._terminalSuggestWidgetContextKeys)) {
                this._loadSuggestAddon(xterm.raw);
            }
        }));
        this.add(this._configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration("terminal.integrated.sendKeybindingsToShell" )) {
                this._loadSuggestAddon(xterm.raw);
            }
        }));
    }
    _loadSuggestAddon(xterm) {
        const sendingKeybindingsToShell = this._configurationService.getValue(TERMINAL_CONFIG_SECTION).sendKeybindingsToShell;
        if (sendingKeybindingsToShell) {
            this._addon.dispose();
            return;
        }
        if (this._terminalSuggestWidgetVisibleContextKey) {
            this._addon.value = this._instantiationService.createInstance(SuggestAddon, this._instance.capabilities, this._terminalSuggestWidgetVisibleContextKey);
            xterm.loadAddon(this._addon.value);
            this._addon.value.setPanel(findParentWithClass(xterm.element, 'panel'));
            this._addon.value.setScreen(xterm.element.querySelector('.xterm-screen'));
            this.add(this._instance.onDidBlur(() => this._addon.value?.hideSuggestWidget()));
            this.add(this._addon.value.onAcceptedCompletion(async (text) => {
                this._instance.focus();
                this._instance.sendText(text, false);
            }));
            this.add(this._instance.onDidSendText(() => this._addon.value?.hideSuggestWidget()));
        }
    }
};
TerminalSuggestContribution = TerminalSuggestContribution_1 = ( (__decorate([
    ( (__param(3, IContextKeyService))),
    ( (__param(4, IConfigurationService))),
    ( (__param(5, IInstantiationService)))
], TerminalSuggestContribution)));
registerTerminalContribution(TerminalSuggestContribution.ID, TerminalSuggestContribution);
registerActiveInstanceAction({
    id: "workbench.action.terminal.selectPrevSuggestion" ,
    title: ( localize2WithPath(_moduleId, 0, 'Select the Previous Suggestion')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 16 ,
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.selectPreviousSuggestion()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.selectPrevPageSuggestion" ,
    title: ( localize2WithPath(_moduleId, 1, 'Select the Previous Page Suggestion')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 11 ,
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.selectPreviousPageSuggestion()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.selectNextSuggestion" ,
    title: ( localize2WithPath(_moduleId, 2, 'Select the Next Suggestion')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 18 ,
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.selectNextSuggestion()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.selectNextPageSuggestion" ,
    title: ( localize2WithPath(_moduleId, 3, 'Select the Next Page Suggestion')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 12 ,
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.selectNextPageSuggestion()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.acceptSelectedSuggestion" ,
    title: ( localize2WithPath(_moduleId, 4, 'Accept Selected Suggestion')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 3 ,
        secondary: [2 ],
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.acceptSelectedSuggestion()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.hideSuggestWidget" ,
    title: ( localize2WithPath(_moduleId, 5, 'Hide Suggest Widget')),
    f1: false,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalContextKeys.focus, TerminalContextKeys.isOpen, TerminalContextKeys.suggestWidgetVisible))),
    keybinding: {
        primary: 9 ,
        weight: 200  + 1
    },
    run: (activeInstance) => TerminalSuggestContribution.get(activeInstance)?.addon?.hideSuggestWidget()
});
